import { track } from '@ramp106/omrjs-tracking-reviews'
import { storeToRefs } from 'pinia'
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { FEATURE_FLAG } from '@/composables/featureFlagCatalog'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import { ReadStatus, useMarkAsReadMutation, useMarkAsUnreadMutation, useNotificationsQuery } from '@/gql/platform-api'
import { showNotification } from '@/helpers/notificationHelper'
import { $can } from '@/services/roles'
import { useLocaleStore, useNotificationCenterStore } from '@/stores'

const PREVIEW_SIZE = 3

const trackAction = (ctaId: string) =>
  track('cta_click', { surface: 'dashboard', category: 'engagement', cta_position: 'dashboard_notifications', cta_id: ctaId })

export const useNotificationCenter = ({ refetchOnLocaleChange = false }: { refetchOnLocaleChange?: boolean } = {}) => {
  const store = useNotificationCenterStore()
  const { notifications, unreadCount, loading } = storeToRefs(store)

  const { enabled: feedEnabled } = useFeatureFlag(FEATURE_FLAG.NOTIFICATIONS.FEED_RELEASE)
  const { t } = useI18n()
  const { mutate: markAsReadMutate } = useMarkAsReadMutation({ clientId: 'platformApi' })
  const { mutate: markAsUnreadMutate } = useMarkAsUnreadMutation({ clientId: 'platformApi' })

  const {
    result,
    refetch,
    loading: queryLoading,
  } = useNotificationsQuery({ first: PREVIEW_SIZE, readStatus: ReadStatus.Unread }, () => ({
    clientId: 'platformApi',
    fetchPolicy: 'network-only',
    enabled: feedEnabled.value && $can('manageReviews', { for: 'User' }),
  }))

  const reload = async () => {
    store.requestUnreadCountSync()
    await refetch()
  }

  watch(queryLoading, (v) => store.setLoading(v), { immediate: true })
  watch(
    result,
    (v) => {
      if (!v?.public) return
      store.setFeed(v.public.notifications)
      store.syncUnreadCountFromQuery(v.public.notifications.unreadCount)
    },
    { immediate: true },
  )

  if (refetchOnLocaleChange) {
    const { locale } = storeToRefs(useLocaleStore())
    watch(locale, () => {
      reload()
    })
  }

  const markAsRead = async (id: string) => {
    const target = notifications.value.find((item) => item.id === id)
    if (!target || target.readAt) return
    try {
      await markAsReadMutate({ ids: [id] })
      store.applyRead(id)
    } catch {
      showNotification(t('Dashboard.notifications.actionError'), 'error', 10000)
      return
    }
    await reload().catch(() => undefined)
  }

  const markAsUnread = async (id: string) => {
    try {
      await markAsUnreadMutate({ ids: [id] })
      store.applyUnread(id)
    } catch {
      showNotification(t('Dashboard.notifications.actionError'), 'error', 10000)
    }
  }

  const toggleRead = async (id: string) => {
    const target = notifications.value.find((item) => item.id === id)
    if (!target) return
    trackAction(target.readAt ? 'notification_mark_unread' : 'notification_mark_read')
    if (!target.readAt) {
      await markAsRead(id)
      return
    }

    await markAsUnread(id)
  }

  const handleCtaClick = async (id: string) => {
    const target = notifications.value.find((item) => item.id === id)
    if (target) track('notification_click', { surface: 'dashboard', notification_category: target.category, notification_id: target.uuid })
    await markAsRead(id)
  }

  const trackViewAll = () => trackAction('notification_view_all')

  return { notifications, unreadCount, loading, markAsRead, toggleRead, handleCtaClick, trackViewAll }
}
